'use client';
/* APERÇU ANIMÉ D'UN DOCUMENT (RQ, 19/10/2026).
 *
 * Une image — un GIF qui enchaîne les écrans d'un mode opératoire, le plus
 * souvent — qui RÉSUME le document. Elle s'affiche EN PLEIN ÉCRAN au survol (ou au focus
 * clavier) d'une petite icône, dans la liste des documents et sur la fiche.
 * Elle n'est pas le document : hors circuit, hors version, hors aperçu officiel.
 * Les règles (qui dépose, qui retire) sont côté serveur, `backend/src/apercu-anime.ts`.
 *
 * L'image ne se charge qu'au premier survol : une liste de cent documents ne
 * télécharge pas cent animations. */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGed, userName } from '@/lib/store';
import { refusDepot, poidsMo } from '@/lib/depot';
import type { GedDocument } from '@/lib/types';
import { Bouton } from '@/shared/Bouton';
import { inputStyle, COUCHES } from '@/components/ui';
import { fmtDate } from '@/lib/meta';

const src = (doc: Pick<GedDocument, 'documentId' | 'apercu'>) =>
  `/api/ged/ged/docs/${doc.documentId}/apercu-anime?v=${doc.apercu?.empreinte || ''}`;

function Pictogramme({ taille }: { taille: number }) {
  return (
    <svg width={taille} height={taille} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <path d="M10 9.2v5.6l4.6-2.8z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** L'icône et son aperçu PLEIN ÉCRAN (RQ, 20/10/2026 : « trop petit, il faudrait
 *  qu'il occupe tout l'écran »). Survol ou focus : l'aperçu s'affiche tant que la
 *  souris reste sur l'icône (le voile laisse passer la souris). Clic : il reste
 *  ouvert, et se ferme par Échap ou un clic n'importe où. Ne rend rien si le
 *  document n'a pas d'aperçu. */
export function IconeApercu({ doc, taille = 16 }: { doc: Pick<GedDocument, 'documentId' | 'code' | 'titre' | 'apercu'>; taille?: number }) {
  const [survol, setSurvol] = useState(false);
  const [epingle, setEpingle] = useState(false);
  const ouvert = survol || epingle;

  useEffect(() => {
    if (!epingle) return;
    const echap = (e: KeyboardEvent) => { if (e.key === 'Escape') { setEpingle(false); setSurvol(false); } };
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, [epingle]);

  if (!doc.apercu) return null;
  const fermer = () => { setEpingle(false); setSurvol(false); };

  return (
    <>
      <span role="button" tabIndex={0}
        aria-label={`Aperçu animé de ${doc.code} — résumé du document, en plein écran`}
        onMouseEnter={() => setSurvol(true)} onMouseLeave={() => setSurvol(false)}
        onFocus={() => setSurvol(true)} onBlur={() => setSurvol(false)}
        onClick={(e) => { e.stopPropagation(); setEpingle((v) => !v); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') fermer();
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEpingle((v) => !v); }
        }}
        style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--cyan-700)', cursor: 'zoom-in', flex: 'none' }}>
        <Pictogramme taille={taille} />
      </span>
      {ouvert && typeof document !== 'undefined' && createPortal(
        <div role="dialog" aria-label={`Aperçu animé de ${doc.code}`}
          onClick={fermer}
          style={{
            position: 'fixed', inset: 0, zIndex: COUCHES.PLEIN_ECRAN,
            // Au survol, le voile laisse passer la souris : quitter l'icône ferme l'aperçu.
            pointerEvents: epingle ? 'auto' : 'none', cursor: epingle ? 'zoom-out' : undefined,
            background: 'rgba(0,0,0,.72)', display: 'grid', gridTemplateRows: 'auto 1fr', gap: 10,
            padding: '14px 20px 20px',
          }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', color: '#fff', fontSize: 14, minWidth: 0 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{doc.code}</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{doc.titre} — aperçu animé</span>
            <span style={{ fontSize: 12.5, opacity: .8, flex: 'none' }}>
              {epingle ? 'Échap ou clic pour fermer' : 'Cliquez l’icône pour garder l’aperçu ouvert'}
            </span>
          </div>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={src(doc)} alt={`Aperçu animé de ${doc.code}`}
            style={{ width: '100%', height: '100%', minHeight: 0, objectFit: 'contain' }} />
        </div>,
        document.body,
      )}
    </>
  );
}

/** Le panneau de l'onglet « Fichier » : voir, déposer, remplacer, retirer l'aperçu. */
export function GestionApercu({ doc }: { doc: GedDocument }) {
  const { session, settings, act, showToast } = useGed();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [retrait, setRetrait] = useState(false);
  const [motif, setMotif] = useState('');

  // Même règle que le serveur (qui re-vérifie) : voir `backend/src/apercu-anime.ts`.
  const estRq = session.roles.includes('rq');
  const affectes = (doc.redacteurs?.length ? doc.redacteurs : [doc.redacteur]).filter(Boolean) as string[];
  const fige = ['obsolete', 'retire', 'abandonne'].includes(doc.statut);
  const peutDeposer = !fige && (estRq || (affectes.includes(session.key) && ['brouillon', 'enrevision'].includes(doc.statut)));
  const a = doc.apercu;
  if (!a && !peutDeposer) return null;

  const deposer = async (file: File) => {
    const refus = await refusDepot('apercuDocument', file, 'L’aperçu animé');
    if (refus) { showToast(refus, 'erreur'); return; }
    setBusy(true);
    try {
      const base64 = btoa(new Uint8Array(await file.arrayBuffer()).reduce((s, b) => s + String.fromCharCode(b), ''));
      if (await act(doc.documentId, 'apercu-anime', { name: file.name, base64 })) {
        showToast(a ? 'Aperçu animé remplacé — l’ancien est conservé.' : 'Aperçu animé déposé : il s’affiche au survol de l’icône, dans la liste et sur la fiche.');
      }
    } finally { setBusy(false); if (input.current) input.current.value = ''; }
  };

  const retirer = async () => {
    setBusy(true);
    try {
      if (await act(doc.documentId, 'apercu-anime/retirer', { motif })) {
        showToast('Aperçu animé retiré — l’image est conservée à part, le journal le dit.');
        setRetrait(false); setMotif('');
      }
    } finally { setBusy(false); }
  };

  return (
    <section style={{ margin: '0 0 14px', padding: '12px 14px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>Aperçu animé</strong>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: 1, minWidth: 220 }}>
          {a
            ? <>Survolez l’icône pour le voir. {a.name} · {poidsMo(a.size)} · déposé par {userName(settings, a.updatedBy)} le {fmtDate(a.updatedAt)}.</>
            : 'Une image animée (GIF) qui résume le document, montrée au survol d’une icône dans la liste et sur la fiche. Elle n’entre pas dans le circuit de validation.'}
        </span>
        {a && <IconeApercu doc={doc} taille={20} />}
        {peutDeposer && (
          <>
            <input ref={input} type="file" accept=".gif,.png,.webp,.jpg,.jpeg,image/gif,image/png,image/webp,image/jpeg" hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void deposer(f); }} />
            <Bouton taille="sm" chargement={busy} onClick={() => input.current?.click()}>{a ? 'Remplacer' : 'Déposer un aperçu'}</Bouton>
          </>
        )}
        {a && estRq && !retrait && <Bouton taille="sm" onClick={() => setRetrait(true)}>Retirer</Bouton>}
      </div>
      {retrait && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Motif du retrait (5 caractères au moins)"
            style={{ ...inputStyle, flex: 1, minWidth: 240 }} />
          <Bouton taille="sm" variante="danger" disabled={motif.trim().length < 5} chargement={busy} onClick={() => void retirer()}>Confirmer le retrait</Bouton>
          <Bouton taille="sm" variante="discret" onClick={() => { setRetrait(false); setMotif(''); }}>Annuler</Bouton>
        </div>
      )}
    </section>
  );
}
